import { Flex, SimpleGrid } from '@chakra-ui/react';
import { useMemo, useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatCurrency, formatNumber, SummaryCards } from '@aslanpay/portal-common-lib';
import AuthenticatedPageWrapper from 'app/components/UI/AuthenticatedPageWrapper';
import { useManagingCompanyStore } from 'app/store';
import usePots, { companyPotQueryParams } from 'app/hooks/usePots';
import useGroups from 'app/hooks/useGroups';
import useRecognitionDashboard from 'app/hooks/useRecognitionDashboard';
import getPointsValue from 'app/utils/points';

import { getRecognitionPath, RecognitionDestination } from '../destinations';

import RecognitionStatCards from './RecognitionStatCards';
import QuickActions from './QuickActions';
import ValuesLeaderboard from './ValuesLeaderboard';
import EmployeeLeaderboard from './EmployeeLeaderboard';
import GroupLeaderboard from './GroupLeaderboard';
import { LeaderboardMetric } from './LeaderboardTabs';
import LeaderboardModal, { LeaderboardKind } from './LeaderboardModal/LeaderboardModal';
import RecognitionInsightsEmpty from './RecognitionInsightsEmpty';

interface SeeAllState {
  kind: LeaderboardKind;
  cardTitle: string;
  metric: LeaderboardMetric;
}

const LEADERBOARD_FETCH_LIMIT = 6;
const LEADERBOARD_DISPLAY_LIMIT = 5;

function Overview() {
  const navigate = useNavigate();
  const company = useManagingCompanyStore((state) => state.company);
  const companyId = company?.id;

  const handleNavigate = useCallback(
    (destination: RecognitionDestination) => {
      if (!companyId) return;
      navigate(getRecognitionPath(destination, companyId));
    },
    [navigate, companyId]
  );
  const [seeAll, setSeeAll] = useState<SeeAllState | null>(null);
  const openSeeAll = (kind: LeaderboardKind, cardTitle: string) => (metric: LeaderboardMetric) =>
    setSeeAll({ kind, cardTitle, metric });

  const { data: companyGeneralPot, isLoading: loadingBags } = usePots.useFetchPots(
    companyPotQueryParams(companyId),
    !!companyId
  );

  const { data: companyPots, isLoading: loadingPots } = usePots.useFetchPots(
    {
      companyIds: companyId ? [companyId] : [],
      status: ['ACTIVE'],
      relatedType: ['GROUP'],
      page: 0,
      size: 1,
    },
    !!companyId
  );

  const { data: companyGroups, isLoading: loadingGroups } = useGroups.useFetchGroups(
    {
      companyId: companyId || '',
      archived: false,
      page: 0,
      size: 1,
    },
    !!companyId
  );

  const { data: recognitionOverview } = useRecognitionDashboard.useFetchRecognitionOverview(
    companyId || '',
    !!companyId
  );

  const { data: valuesLeaderboard } = useRecognitionDashboard.useFetchValuesLeaderboard(
    companyId || '',
    LEADERBOARD_FETCH_LIMIT,
    !!companyId
  );

  const { data: recognisedEmployees } = useRecognitionDashboard.useFetchMostRecognisedEmployees(
    companyId || '',
    LEADERBOARD_FETCH_LIMIT,
    !!companyId
  );

  const { data: engagedEmployees } = useRecognitionDashboard.useFetchMostEngagedEmployees(
    companyId || '',
    LEADERBOARD_FETCH_LIMIT,
    !!companyId
  );

  const { data: groupsLeaderboard } = useRecognitionDashboard.useFetchGroupsLeaderboard(
    companyId || '',
    LEADERBOARD_FETCH_LIMIT,
    !!companyId
  );

  const { data: potsLeaderboard } = useRecognitionDashboard.useFetchPotsLeaderboard(
    companyId || '',
    LEADERBOARD_FETCH_LIMIT,
    !!companyId
  );

  const summaryCards = useMemo(() => {
    const walletBalance = companyGeneralPot?.list?.[0]?.balance || 0;
    const totalPots = companyPots?.total ?? 0;
    const totalGroups = companyGroups?.total ?? 0;

    return [
      {
        title: 'Company wallet',
        value: formatNumber(walletBalance),
        description: `worth ${formatCurrency(getPointsValue(walletBalance))}`,
        onClick: () => handleNavigate('walletsAndPots'),
      },
      {
        title: 'Active pots',
        value: totalPots,
        description: 'Click to manage pots',
        onClick: () => handleNavigate('walletsAndPots'),
      },
      {
        title: 'Total prides',
        value: totalGroups,
        description: 'Click to manage prides',
        onClick: () => handleNavigate('prides'),
      },
    ];
  }, [companyGeneralPot, companyPots, companyGroups, handleNavigate]);

  const isLoadingSummary = loadingBags || loadingPots || loadingGroups;

  const valueEntries = valuesLeaderboard?.entries.slice(0, LEADERBOARD_DISPLAY_LIMIT) ?? [];
  const hasMoreValues = (valuesLeaderboard?.entries.length ?? 0) > LEADERBOARD_DISPLAY_LIMIT;

  const recognisedEntries = recognisedEmployees?.entries.slice(0, LEADERBOARD_DISPLAY_LIMIT) ?? [];
  const hasMoreRecognised = (recognisedEmployees?.entries.length ?? 0) > LEADERBOARD_DISPLAY_LIMIT;

  const engagedEntries = engagedEmployees?.entries.slice(0, LEADERBOARD_DISPLAY_LIMIT) ?? [];
  const hasMoreEngaged = (engagedEmployees?.entries.length ?? 0) > LEADERBOARD_DISPLAY_LIMIT;

  const groupEntries = groupsLeaderboard?.entries.slice(0, LEADERBOARD_DISPLAY_LIMIT) ?? [];
  const hasMoreGroups = (groupsLeaderboard?.entries.length ?? 0) > LEADERBOARD_DISPLAY_LIMIT;

  const potEntries = potsLeaderboard?.entries.slice(0, LEADERBOARD_DISPLAY_LIMIT) ?? [];
  const hasMorePots = (potsLeaderboard?.entries.length ?? 0) > LEADERBOARD_DISPLAY_LIMIT;

  return (
    <AuthenticatedPageWrapper title="Overview">
      <Flex flexDir="column" gap="20px" w="full">
        {!isLoadingSummary && <SummaryCards cards={summaryCards} />}
        {recognitionOverview && (
          <RecognitionStatCards data={recognitionOverview} onNavigate={handleNavigate} />
        )}
        <QuickActions onNavigate={handleNavigate} />
        <ValuesLeaderboard
          entries={valueEntries}
          hasMore={hasMoreValues}
          onSeeAll={() =>
            setSeeAll({ kind: 'values', cardTitle: 'Values leaderboard', metric: 'count' })
          }
        />
        {recognitionOverview && recognitionOverview.totalRecognitions === 0 ? (
          <RecognitionInsightsEmpty />
        ) : (
          <SimpleGrid columns={{ base: 1, md: 2 }} gap="20px">
            <EmployeeLeaderboard
              title="Most recognised employees"
              subtitle="Most recognitions received (top 5)"
              entries={recognisedEntries}
              hasMore={hasMoreRecognised}
              onSeeAll={openSeeAll('recognised', 'Most recognised employees')}
            />
            <EmployeeLeaderboard
              title="Most engaged employees"
              subtitle="Most recognitions sent (top 5)"
              entries={engagedEntries}
              hasMore={hasMoreEngaged}
              onSeeAll={openSeeAll('engaged', 'Most engaged employees')}
            />
            <GroupLeaderboard
              title="Engagement by pride"
              subtitle="Activity across prides (top 5)"
              entries={groupEntries}
              hasMore={hasMoreGroups}
              onSeeAll={openSeeAll('groups', 'Engagement by pride')}
            />
            <GroupLeaderboard
              title="Engagement by pot"
              subtitle="Activity across pots (top 5)"
              entries={potEntries}
              hasMore={hasMorePots}
              onSeeAll={openSeeAll('pots', 'Engagement by pot')}
            />
          </SimpleGrid>
        )}
      </Flex>
      {seeAll && (
        <LeaderboardModal
          isOpen
          onClose={() => setSeeAll(null)}
          kind={seeAll.kind}
          cardTitle={seeAll.cardTitle}
          metric={seeAll.metric}
        />
      )}
    </AuthenticatedPageWrapper>
  );
}

export default Overview;
